import { TextField, InputAdornment } from '@mui/material'
import { alpha, styled } from '@mui/material/styles'

/**
 * Centralized TextField styled components
 * Use these instead of repeating TextField styles across components
 */

/**
 * Standard form TextField with theme-aware styling
 * Used across Deals, Organizations, Contacts, Leads
 */
export const FormTextField = styled(TextField)(({ theme }) => ({
  backgroundColor: theme.palette.background.section,
  textAlign: 'left',
  borderColor: alpha(theme.palette.text.primary, 0.12),
  borderWidth: '1px',
  borderStyle: 'solid',
  borderRadius: '4px',

  '&:hover': {
    borderColor: alpha(theme.palette.text.primary, 0.32),
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: alpha(theme.palette.text.primary, 0.12),
    },
    '&:hover fieldset': {
      borderWidth: '0px !important',
    },
    '&.Mui-focused fieldset': {
      borderWidth: '1px !important',
      borderColor: theme.palette.text.primary,
    },
  },
  '& .MuiOutlinedInput-root.Mui-focused': {},
  '& .MuiOutlinedInput-input': {
    '&:focus': {
      backgroundColor: theme.palette.background.input,
    },
  },
}))

/**
 * Deal-only TextField with golden label
 */
export const DealTextField = styled(TextField)(({ theme }) => ({
  backgroundColor: theme.palette.background.defaultdim,
  '& .MuiInputLabel-root': {
    color: 'rgba(249, 180, 45, 1)',
    '&.Mui-focused': { color: 'rgba(249, 180, 45, 1)' },
  },
  '& .MuiInputBase-input': {
    fontSize: 14,
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiInputBase-input': {
    color: '#fff',
  },
}))

/**
 * Currency input adornment with focus styling
 */
export const CurrencyAdornment = styled(InputAdornment)(({ theme }) => ({
  alignSelf: 'stretch',
  display: 'flex',
  alignItems: 'center',
  borderTopLeftRadius: 8,
  borderBottomLeftRadius: 8,

  '.MuiOutlinedInput-root.Mui-focused &': {
    backgroundColor: theme.palette.background.section,
    color: '#fff',
  },

  '.MuiOutlinedInput-root.Mui-disabled &': {
    backgroundColor: 'transparent',
    color: theme.palette.text.disabled,
  },
}))

/**
 * Fee field styles (for use with sx prop)
 */
export const feeFieldStyles = (fieldBgColor: string) => ({
  '& .MuiFormLabel-root': {
    fontWeight: 'bold',
    color: 'rgba(249, 180, 45, 1)',
    '&': {
      color: 'rgba(255,255,255)',
      backgroundColor: fieldBgColor,
    },
  },
  '& .MuiInputBase-input': {
    border: 'none',
  },
  '& .MuiInputBase-root': {
    alignItems: 'center',
    fontSize: '14px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
    borderColor: 'transparent',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: 'none',
    borderColor: 'transparent',
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    border: 'none',
    borderColor: 'transparent',
  },
  '& .MuiInputLabel-root': {
    fontSize: '12px',
  },
  '& .MuiInputLabel-shrink': {
    fontSize: '14px',
  },
})

/**
 * Read-only totals field styles (for use with sx prop)
 */
export const totalsReadOnlyFieldStyles = (_fieldBgColor: string) => ({
  '& .MuiInputBase-root': {
    pointerEvents: 'none' as const,
    border: 'none',
  },
  '& .MuiInputBase-input': {
    border: 'none',
    fontSize: '14px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
    borderColor: 'transparent',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: 'none',
    borderColor: 'transparent',
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    border: 'none',
    borderColor: 'transparent',
  },
  '& .MuiInputLabel-root': {
    fontSize: '12px',
  },
  '& .MuiInputLabel-shrink': {
    fontSize: '14px',
  },
})

/**
 * Basic themed input colors (for use with sx prop)
 */
export const themedInputStyles = (theme: { palette: { mode: string; text: { primary: string }; background: { input?: string } } }) => ({
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.input,
  borderColor: alpha(theme.palette.text.primary, 0.3),
})
